import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import SavedSearchSelect from '../SavedSearchSelect';
import SearchMyMemberMatches from '../../searches/SearchMyMemberMatches';
import ArrowRightIcon from '@icons/ArrowRightIcon';
import LoadingArea from '@molecules/LoadingArea';
import { BodyLarge, ButtonLink, LinkSmall } from '@styled';

// Functionality
import { getSavedSearchTitle } from '../../../helpers/savedSearches.helpers';
import useSavedSearches from '../../../hooks/useSavedSearches';
import { mediaQueries } from '@general/breakpoints';
import colors from '@general/colors';

const HeaderContainer = styled.div`
    @media ${mediaQueries.md} {
        align-items: flex-start;
        display: flex;
        justify-content: space-between;
    }
`;

const HeaderCTAContainer = styled.div`
    padding-top: 4px;

    @media ${mediaQueries.md} {
        display: flex;
    }
`;

const EventAttendeesMatches = ({ event, eventCode, openScheduleAppointmentModal }) => {
    const history = useHistory();

    // -----------------------------
    // State
    // -----------------------------

    const [selectedSearch, setSelectedSearch] = useState();

    const { data: savedSearchesData, loading: savedSearchesLoading } = useSavedSearches({
        onCompleted: ({ savedSearches }) => {
            if (selectedSearch) {
                return;
            }

            if (savedSearches.length > 0) {
                setSelectedSearch(savedSearches[0]);
            }
        },
    });

    // -----------------------------
    // Render
    // -----------------------------

    if (savedSearchesLoading) {
        return <LoadingArea />;
    }

    return (
        <div>
            {!selectedSearch && (
                <>
                    <BodyLarge className="mb-1rem">
                        Get the most out of ICSC networking by leveraging contact recommendations. Create Match criteria
                        based on what you're looking for and ICSC will connect you to the right members.
                    </BodyLarge>

                    <ButtonLink
                        href="/my-icsc/connections/matches/new"
                        onClick={e => {
                            e.preventDefault();
                            history.push('/my-icsc/connections/matches/new');
                        }}
                    >
                        Create Your First Set of Match Criteria
                    </ButtonLink>
                </>
            )}

            {selectedSearch && (
                <>
                    <HeaderContainer className="mb-2rem">
                        <BodyLarge className="pr-1.75rem">
                            Attendees matching "{getSavedSearchTitle(selectedSearch)}"
                        </BodyLarge>

                        <HeaderCTAContainer>
                            <SavedSearchSelect
                                savedSearches={savedSearchesData.savedSearches}
                                selectedSearch={selectedSearch}
                                onSavedSearchSelect={setSelectedSearch}
                            />

                            <LinkSmall
                                className="align-items-center cursor-pointer d-flex text-primary whitespace-nowrap"
                                href={event.url.replace('/details/', '/attendees/')}
                            >
                                <span className="mr-0.5rem">See Full Attendee List</span>
                                <ArrowRightIcon color={colors.primary} />
                            </LinkSmall>
                        </HeaderCTAContainer>
                    </HeaderContainer>

                    <SearchMyMemberMatches
                        savedSearch={selectedSearch}
                        additionalFilters={`events:${eventCode}`}
                        parentView="Events"
                        showCalendarIcon
                        showSearchBox={false}
                        showSearchOptions={false}
                        hitsPerPage={6}
                        NoResultsText={() => (
                            <>Looks like no matches yet. Check back closer to the event or create new match criteria.</>
                        )}
                        onCalendarClick={member => {
                            openScheduleAppointmentModal(member, 'saved_searchable_members');
                        }}
                    />
                </>
            )}
        </div>
    );
};

EventAttendeesMatches.propTypes = {
    event: PropTypes.shape({
        url: PropTypes.string.isRequired,
    }).isRequired,
    eventCode: PropTypes.string.isRequired,
    openScheduleAppointmentModal: PropTypes.func,
};

export default EventAttendeesMatches;
